import { Link } from 'react-router-dom';
import { Eyebrow } from '@/components/content/Eyebrow';
import { TwoDoorsBlock } from '@/components/content/TwoDoorsBlock';
import { Button } from '@/components/interactive/Button';
import { Reveal } from '@/components/utils/RevealOnScroll';
import { ORGANIZATION_LD, SEO } from '@/components/utils/SEO';

const PLATFORM_GITHUB_URL = 'https://github.com/Unicorn/Radium';

function SharedHero() {
  return (
    <section
      className="page-hero--tall"
      style={{
        position: 'relative',
        paddingBottom: 64,
        overflow: 'hidden',
      }}
    >
      <div className="container" style={{ position: 'relative', zIndex: 1 }}>
        <Reveal>
          <Eyebrow symbol="Ra" number="88" label="Radium" />
        </Reveal>
        <Reveal delay={80}>
          <h1
            className="text-display"
            style={{ marginTop: 24, maxWidth: 1100 }}
          >
            Open-source orchestration.
            <br />
            <span style={{ color: 'var(--accent)' }}>
              Enterprise personalization.
            </span>
          </h1>
        </Reveal>
        <Reveal delay={200}>
          <p className="text-body-lg" style={{ marginTop: 28, maxWidth: 760 }}>
            One platform. Two products. Built by Unicorn.
          </p>
        </Reveal>
        <Reveal delay={320}>
          <div
            style={{
              marginTop: 36,
              display: 'flex',
              gap: 16,
              flexWrap: 'wrap',
            }}
          >
            <Button href="#two-doors" variant="primary" size="lg" chevron>
              Explore Radium Orchestration
            </Button>
            <Button href="#two-doors" variant="secondary" size="lg" chevron>
              See Radium Personalization
            </Button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

interface ProofCardProps {
  tag: string;
  metric: string;
  metricSub?: string;
  story: string;
  cta: { to: string; label: string; external?: boolean };
}

function ProofCard({ tag, metric, metricSub, story, cta }: ProofCardProps) {
  const inner = (
    <>
      <div
        className="text-mono"
        style={{
          fontSize: 11,
          color: 'var(--ink-muted)',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
        }}
      >
        {tag}
      </div>
      <div style={{ marginTop: 24, marginBottom: 24 }}>
        <div
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: metric.length > 12 ? 28 : 40,
            fontWeight: 600,
            lineHeight: 1.1,
            color: 'var(--ink-primary)',
            letterSpacing: '-0.012em',
          }}
        >
          {metric}
        </div>
        {metricSub && (
          <div
            style={{
              marginTop: 6,
              color: 'var(--ink-muted)',
              fontSize: 14,
              fontFamily: 'var(--font-mono)',
            }}
          >
            {metricSub}
          </div>
        )}
      </div>
      <p
        style={{
          color: 'var(--ink-secondary)',
          fontSize: 15,
          lineHeight: 1.6,
          marginBottom: 20,
        }}
      >
        {story}
      </p>
      <span
        style={{
          color: 'var(--accent)',
          fontSize: 14,
          fontWeight: 500,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        {cta.label} <span aria-hidden="true">→</span>
      </span>
    </>
  );

  const sharedStyle: React.CSSProperties = {
    display: 'block',
    padding: 32,
    borderRadius: 12,
    height: '100%',
    textDecoration: 'none',
  };

  return cta.external ? (
    <a
      href={cta.to}
      target="_blank"
      rel="noopener noreferrer"
      className="proof-card glass"
      style={sharedStyle}
    >
      {inner}
    </a>
  ) : (
    <Link to={cta.to} className="proof-card glass" style={sharedStyle}>
      {inner}
    </Link>
  );
}

function ProofBand() {
  return (
    <section
      className="section"
      style={{
        borderTop: '1px solid var(--border-subtle)',
      }}
    >
      <div className="container">
        <Reveal>
          <Eyebrow>Proof, on both sides</Eyebrow>
          <h2 className="text-h2" style={{ marginTop: 16, maxWidth: 760 }}>
            One platform underneath. Two stories on top.
          </h2>
        </Reveal>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 24,
            marginTop: 48,
            alignItems: 'stretch',
          }}
          className="proof-grid"
        >
          <Reveal delay={80}>
            <ProofCard
              tag="Open source"
              metric="MIT-licensed"
              metricSub="github.com/Unicorn/Radium"
              story="The Radium runtime, policy engine, ACE Skillbook, and Vibe Check are all open source. Read the source. Self-host it. Audit every decision the system makes."
              cta={{
                to: PLATFORM_GITHUB_URL,
                label: 'Star on GitHub',
                external: true,
              }}
            />
          </Reveal>
          <Reveal delay={160}>
            <ProofCard
              tag="In production"
              metric="Deployed"
              metricSub="financial wellness · editorial publishing"
              story="Radium for Personalization is running in financial wellness and editorial publishing. Editorial keeps the final word. Compliance gets the audit trail. Engineering isn't on call for tagging."
              cta={{
                to: '/personalization/customers',
                label: 'Read the case studies',
              }}
            />
          </Reveal>
        </div>
      </div>
      <style>{`
        @media (max-width: 880px) {
          .proof-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
}

function WhyRadiumExists() {
  return (
    <section
      className="section"
      style={{ borderTop: '1px solid var(--border-subtle)' }}
    >
      <div className="container container-narrow">
        <Reveal>
          <Eyebrow>Why Radium exists</Eyebrow>
          <h2 className="text-h2" style={{ marginTop: 16 }}>
            Unicorn turns its consulting work into platforms.
          </h2>
        </Reveal>
        <Reveal delay={120}>
          <p
            style={{
              marginTop: 24,
              color: 'var(--ink-secondary)',
              fontSize: 18,
              lineHeight: 1.65,
            }}
          >
            Unicorn is an applied venture studio. We work alongside teams on
            the hardest parts of their stack — content systems, data pipelines,
            agentic infrastructure — and the patterns that recur, we
            productize. Radium is the orchestration substrate that came out of
            years of that work. Radium for Personalization is the first
            enterprise application built on it. There will be more.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function ClosingDualCTA() {
  return (
    <section
      className="section"
      style={{
        borderTop: '1px solid var(--border-subtle)',
        textAlign: 'center',
      }}
    >
      <div className="container container-narrow">
        <Reveal>
          <h2 className="text-h2" style={{ maxWidth: 760, margin: '0 auto' }}>
            Bring your stack.{' '}
            <span style={{ color: 'var(--accent)' }}>
              We'll bring the reaction.
            </span>
          </h2>
        </Reveal>
        <Reveal delay={120}>
          <p
            style={{
              marginTop: 20,
              color: 'var(--ink-secondary)',
              fontSize: 17,
              maxWidth: 640,
              margin: '20px auto 0',
            }}
          >
            We don't configure tools around your stack — we build the solution
            around it. Custom infrastructure, fully hosted, handed off working.
            If personalization is on the roadmap, the conversation starts here.
          </p>
        </Reveal>
        <Reveal delay={240}>
          <div
            style={{
              marginTop: 36,
              display: 'flex',
              gap: 16,
              flexWrap: 'wrap',
              justifyContent: 'center',
            }}
          >
            <Button
              href="/contact"
              variant="primary"
              size="lg"
              chevron
            >
              Contact us
            </Button>
            <Button
              href={PLATFORM_GITHUB_URL}
              variant="secondary"
              size="lg"
              chevron
            >
              Star on GitHub
            </Button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export function Home() {
  return (
    <>
      <SEO
        title="Open-source orchestration. Enterprise personalization."
        description="Radium Orchestration is an open-source, multi-agent, production-ready platform. Radium Personalization is the enterprise content-decisioning product built on it. One platform. Two products. Built by Unicorn."
        canonical="https://radium.love/"
        ogImage="/og/og-home.png"
        jsonLd={ORGANIZATION_LD}
      />
      <SharedHero />
      <TwoDoorsBlock />
      <ProofBand />
      <WhyRadiumExists />
      <ClosingDualCTA />
    </>
  );
}
